import { FC, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSetAtom } from "jotai";

import { CleanupTypeEnum, ServiceTaskTypeEnum } from "@/domain/models/common";
import { JunkLoadStatusEnum } from "@/domain/models/job";
import { useTranslator } from "@/i18n/useTranslator";

import { DsIcon, DsText, DsYellowBadge } from "../components/ui";
import { useServiceQuery } from "../data/queries/serviceQueries";
import { TaskQueryKeys, useTaskQuery } from "../data/queries/taskQueries";
import { JobServiceTaskBaseType } from "../domain/task";
import { DriverTaskDetailsDrawer } from "../interactors";
import {
  isDriverReviewingJunkLoadAtom,
  isDriverReviewingTaskAtom,
} from "../stores/atoms";
import { getBinSize } from "../utils/bin";
import { classNames } from "../utils/classNames";
import { formatCleanupType, formatDriverTaskType } from "../utils/strings";

type DriverTaskCardPropsType = JobServiceTaskBaseType & {
  isDisabled?: boolean;
  shouldApplyDisabledStyles?: boolean;
  shouldShowInProgressBadge?: boolean;
  onTaskClick?: () => void;
};

const DriverTaskCard: FC<DriverTaskCardPropsType> = ({
  isDisabled,
  jobId,
  serviceId,
  taskId,
  shouldApplyDisabledStyles = true,
  shouldShowInProgressBadge = true,
  onTaskClick,
}) => {
  const { t } = useTranslator();

  const setIsDriverReviewingTask = useSetAtom(isDriverReviewingTaskAtom);
  const setDriverIsReviewingJunkLoad = useSetAtom(
    isDriverReviewingJunkLoadAtom,
  );

  const { data: task } = useTaskQuery({ jobId, serviceId, taskId });
  const { data: service } = useServiceQuery({ jobId, serviceId });

  if (!task) {
    return null;
  }

  /* If Drop Off / Wait and Load / Exchange, show the size of the bin being dropped off (task.size), 
    if Pick Up / Dump and Return, show the size of the bin being picked up (task.connectedTask.size),
    unless unassigned, in which case show SIZE TBD */
  const binSize = getBinSize(task);

  const handleOnClick = () => {
    if (isDisabled || task.completed) return;

    setIsDriverReviewingTask(false);
    setDriverIsReviewingJunkLoad(false);

    onTaskClick?.();
  };

  //This is the scenario where will drive to dump and return to site
  const isDumpingLoad =
    task.needToDump &&
    service?.junkLoads?.some(
      junkLoad => junkLoad.status === JunkLoadStatusEnum.IN_PROGRESS,
    );

  const isCurrentTask =
    task.started && !task.completed && (!task.needToDump || isDumpingLoad);

  return (
    <>
      <div
        onClick={handleOnClick}
        className={classNames(
          "block relative overflow-hidden hover:cursor-pointer hover:bg-gray-50 rounded-lg border border-gray-300 bg-white mt-1",
          task.completed &&
            "bg-[#DADADA] opacity-50 hover:cursor-default hover:bg-[#DADADA]",
          isDisabled && shouldApplyDisabledStyles && "opacity-50",
          isDisabled && "hover:cursor-default",
        )}>
        <div className="px-4 py-4 sm:px-6 ">
          <div className="flex items-center justify-between">
            <div className="flex-shrink-0 flex">
              <DsText variant="cardTitle">
                {formatDriverTaskType(task.taskId)}
              </DsText>
              {task.taskId === ServiceTaskTypeEnum.JUNKREMOVAL &&
                task.cleanupType !== CleanupTypeEnum.NONE && (
                  <>
                    <DsYellowBadge
                      text={formatCleanupType({
                        cleanupType: task.cleanupType!,
                      })}
                      className="ml-2"
                    />
                  </>
                )}
            </div>
          </div>
          <div className="mt-2 sm:flex sm:justify-between">
            <div className="w-full flex items center flex-col md:flex-row">
              {!task.cleanupType && (
                <p className="flex items-center text-sm text-gray-500 w-full md:w-1/4 lg:w-1/5 mb-2 md:mb-0">
                  <DsIcon icon="Truck" className="mr-2" />
                  {binSize}
                </p>
              )}

              <p className="flex items-center text-sm text-gray-500 w-full md:w-3/4 lg:w-4/5 ">
                <div>
                  <DsIcon icon="LocationMark" className="mr-2" />
                </div>
                {task.address}
              </p>
            </div>
          </div>
        </div>

        {isCurrentTask && shouldShowInProgressBadge && (
          <div className="absolute bottom-0 right-0 bg-[#5CE76F] rounded-tl-xl">
            <DsText variant="badgeText" className="px-3.5 py-px">
              {t("driver_task_card.in_progress")}
            </DsText>
          </div>
        )}
      </div>
    </>
  );
};

export { DriverTaskCard };
